// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld/base/core';
import 'myWorld/controls/mapControl';
import L from 'leaflet';

myw.SelectionMode = myw.MapInteractionMode.extend('SelectionMode', {
    options: {
        clearSelection: true
    },
    /**
     * Map interaction mode that handles a map click by setting the results as the currentFeature(s)
     * @param  {MapControl}  map map to handle user events on
     * @param  {Application} app the current myWorld application
     * @constructs
     * @extends {MapInteractionMode}
     */
    initialize(map, app, options) {
        this.map = map;
        this.app = app;
        this.multiSelectMode = false;
        this.setOptions(options);

        _.bindAll(this, '_onKeyDown', '_onKeyUp');
    },

    /**
     * Enables the mode - makes the map appear clickable
     */
    enable() {
        this.map.getDivElement().on('keydown', this._onKeyDown);
        this.map.getDivElement().on('keyup', this._onKeyUp);

        //set context menu items
        this.app.ready.then(
            this._setContextMenuForSelectionMode.bind(this, this.options.contextMenuItems)
        );

        //add a css class to the map pane so that the mouse cursor changes to pointer mode
        const mapEl = $(this.map.getContainer());
        this.wasClickable = mapEl.hasClass('leaflet-interactive');
        mapEl.addClass('leaflet-interactive');
    },

    /**
     * Disables the mode - makes "clickable" state of the map back to what it was
     */
    disable() {
        this.map.contextmenu.removeAllItems();
        if (!this.wasClickable) {
            //removes a css class to the map pane so that the mouse cursor changes to "drag" mode
            $(this.map.getContainer()).removeClass('leaflet-interactive');
        }
        this.map.getDivElement().off('keydown', this._onKeyDown);
        this.map.getDivElement().off('keyup', this._onKeyUp);
    },

    /**
     * Handle a click on the map by sending a selection request and setting the application's currentFeature(s) from the results
     */
    handleMapClick(event) {
        //notify that a selection has started (ex: so featureDetails can show an appropriate message)
        this.app.fire('selection-started', { origin: 'map' });

        const selectionHandler =
            this.options.selectionHandler || this.processSelectedFeatures.bind(this);

        this.map.selectFeatures(event.latlng).then(selectionHandler);
    },

    /**
     * Called from map control. Deselects clicked feature
     */
    handleFeatureClick(feature, rep, evt) {
        const app = this.app;
        if (this.multiSelectMode) {
            const nonMatchingFeatures = app.currentFeatureSet.items.filter(
                currentFeature => currentFeature.id != feature.id
            );
            L.DomEvent.stopPropagation(evt);
            return app.setCurrentFeatureSet(nonMatchingFeatures, null, false, undefined);
        }
        //single selection mode
        if (feature != app.currentFeature) {
            app.setCurrentFeature(feature, false, true);
            L.DomEvent.stopPropagation(evt);
        }
    },

    /**
     * Handles a ctrl+mousedrag box on the map
     */
    async handleCtrlDragBox(event) {
        //notify that a selection has started (ex: so featureDetails can show an appropriate message)
        this.app.fire('selection-started', { origin: 'map' });

        //Select features
        const features = await this.map.selectBox(event.latLngBounds);

        //Set features to current
        this.app.setCurrentFeatureSet(features, null, false, undefined);
    },

    /**
     * Turns multi select on when ctrl is pressed
     * @private
     */
    _onKeyDown(e) {
        //Only set multiple select when it is not selected from context menu
        //metaKey is mac command key
        if ((e.ctrlKey || e.metaKey) && !this.multiSelectMode && !this.map.multipleSelect) {
            this.setMultipleSelect(true);
        }
    },

    /**
     * Turns off multi select on ctrl key up, if multiple select has not been selected from context menu
     * @private
     */
    _onKeyUp(e) {
        //ctrl key = 17, Left command = 91 in chrome and 224 in firefox
        if ((e.keyCode == 17 || e.keyCode == 91 || e.keyCode == 224) && !this.map.multipleSelect) {
            this.setMultipleSelect(false);
        }
    },

    /**
     * Stores multiple select mode on class
     * @param {boolean} check
     */
    setMultipleSelect(check) {
        this.multiSelectMode = check;
        if (check) this._changeCursorToDefault(false);
        else this._changeCursorToDefault(true);
    },

    /**
     * Sets cursor on map to default or non default (if input false)
     * NB If on chrome with debug window open the cursor will only refresh on mouse move
     * Close debug window and the behavior should be as expected.
     * @param {bool} defaultCursorStyle if true sets cursor to default style
     * @private
     */
    _changeCursorToDefault(defaultCursorStyle) {
        if (!defaultCursorStyle)
            L.DomUtil.addClass(this.map.getContainer(), 'non-default-cursor-enabled');
        else L.DomUtil.removeClass(this.map.getContainer(), 'non-default-cursor-enabled');
    },

    /**
     * Sets currentFeature and currentFeatureSet taking into account previous selections
     * @param {Feature[]} features An array with recent list of selectable features
     */
    processSelectedFeatures(features) {
        //clear the currentFeatureSet as it is always replaced by the results of a selection
        const app = this.app;

        let selectedFeature = null;

        if (!features.length && !this.multiSelectMode) {
            //no features were found by selection and not in multiSelect mode or boxSelecting

            //on some maps we want to clear the current feature, on others we don't (like internals)
            if (this.options.clearSelection) {
                app.setCurrentFeatureSet([]);
            } else {
                //keep the currentFeature
                app.setCurrentFeature(app.currentFeature, false, true);
            }
        } else {
            // Add all of the features as highlighted features.
            const modifiable = app.currentFeatureSet.modifiable !== false;
            if (this.multiSelectMode) {
                if (modifiable) {
                    let currentFeatures = app.currentFeatureSet.items;
                    features = this._unionFeaturesExcludingDuplicates(currentFeatures, features);
                } else return; //FeatureSet isn't modifiable so don't change it
            } else if (features.length == 1) {
                selectedFeature = features[0];
            }
            app.setCurrentFeatureSet(features, selectedFeature, false, undefined);
        }
    },

    /**
     * Concats the current feature set with the selected set, whilst removing any duplicates
     * @param {feature[]} currentFeatures
     * @param {feature[]} features
     * @private
     */
    _unionFeaturesExcludingDuplicates(features1, features2) {
        const urns1 = features1.map(feature => feature.getUrn());
        const urns2 = features2.map(feature => feature.getUrn());
        const intersection = _.intersection(urns1, urns2);
        features2 = features2.filter(feature => !intersection.includes(feature.getUrn()));
        features1 = features1.filter(feature => !intersection.includes(feature.getUrn()));
        return features1.concat(features2);
    },

    /**
     * Configures the map's context menu
     * The context menu is brought up by a right click or long touch
     * @params {Array<object>} [items] Check https://github.com/aratcliffe/Leaflet.contextmenu#usage
     * @private
     */
    _setContextMenuForSelectionMode(items) {
        if (!items) {
            const actions = this.app.options.mapContextMenuActions || [];
            items = this.buildActionMenuItems(actions);
        }
        const menu = this.map.contextmenu;
        menu.removeAllItems();
        items.forEach(item => menu.addItem(item));
    }
});

export default myw.SelectionMode;
